import React from "react";
import { Props } from "./types";
import styles from "./styles.scss";
import { UseCases } from "@planzone/core";
import { useTranslation } from "react-i18next";
import { useModal } from "@root/client/modal";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { MilestoneIcon } from "@common/milestoneIcon";
import { TaskIcon } from "@common/taskIcon";
import { EventIcon } from "@common/eventIcon";
import { project, list } from "@planzone/dto";
import { UnavailabilityIcon } from "./unavailabilityIcon";
import { Safeguard } from "@scenes/safeguard";

function CreationMenu({ project: projectProp }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const scopedProject = projectProp ?? { ...project, uuid: "" };
  const isMultiProject = !projectProp;

  return (
    <Safeguard
      useCases={[
        UseCases.createTask,
        UseCases.createMilestone,
        UseCases.createEvent,
        UseCases.createUnavailability,
      ]}
    >
      {({
        canCreateTask,
        canCreateEvent,
        canCreateUnavailability,
        canCreateMilestone,
      }) =>
        canCreateTask ||
        canCreateEvent ||
        canCreateUnavailability ||
        canCreateMilestone ? (
          <VisibilityWatcher className={styles.creationMenu}>
            {(isVisible, show, hide) => (
              <React.Fragment>
                <button
                  onClick={isVisible ? hide : show}
                  className={styles.button}
                >
                  <div className={styles.iconBox}>+</div>
                  <p>{t("calendar.creationMenu.button")}</p>
                </button>
                {isVisible && (
                  <div className={styles.menu}>
                    {canCreateUnavailability && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createUnavailability,
                            data: undefined,
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <UnavailabilityIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t("calendar.creationMenu.createUnavailability")}
                        </p>
                      </div>
                    )}
                    {canCreateTask && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createTask,
                            data: {
                              project: scopedProject,
                              list: { ...list, uuid: "" },
                              isMultiProject,
                            },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <TaskIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t("calendar.creationMenu.createTask")}
                        </p>
                      </div>
                    )}
                    {canCreateEvent && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createEvent,
                            data: {
                              project: scopedProject,
                              list: { ...list, uuid: "" },
                              isMultiProject,
                            },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <EventIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t("calendar.creationMenu.createEvent")}
                        </p>
                      </div>
                    )}
                    {canCreateMilestone && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createMilestone,
                            data: {
                              project: scopedProject,
                              list: { ...list, uuid: "" },
                              isMultiProject,
                            },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <MilestoneIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t("calendar.creationMenu.createMilestone")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            )}
          </VisibilityWatcher>
        ) : (
          <React.Fragment />
        )
      }
    </Safeguard>
  );
}

export { CreationMenu };
